import { EditorView } from 'prosemirror-view'

const kAnchorWords = 7
const kContextChars = 200

type AnchorResult = {
  anchorStart: string
  anchorEnd: string
}

/**
 * Builds anchor strings for the backend to locate the edit region.
 *
 * - anchorStart: first kAnchorWords of the selection (or padded with preceding context for short selections)
 * - anchorEnd: last kAnchorWords of the selection (or padded with following context for short selections)
 *
 * The BE uses these to find the edit region in the full document and returns the
 * complete updated markdown. The FE then replaces the entire editor content.
 */
export function getAnchors(view: EditorView, from: number, to: number): AnchorResult | null {
  const docSize = view.state.doc.content.size

  // Get the selected text (empty for cursor clicks)
  const selectedText = from === to ? '' : view.state.doc.textBetween(from, to, ' ')
  const selectedWords = selectedText.trim().split(/\s+/).filter(Boolean)

  // Get context before the selection
  const beforeStart = Math.max(0, from - kContextChars)
  const beforeText = view.state.doc.textBetween(beforeStart, from, ' ')
  const beforeWords = beforeText.trim().split(/\s+/).filter(Boolean)

  // Get context after the selection
  const afterEnd = Math.min(to + kContextChars, docSize)
  const afterText = view.state.doc.textBetween(to, afterEnd, ' ')
  const afterWords = afterText.trim().split(/\s+/).filter(Boolean)

  if (selectedWords.length >= kAnchorWords) {
    // Selection has enough words — use first/last kAnchorWords from within it.
    // For shorter selections the anchors may overlap, which is fine.
    return {
      anchorStart: selectedWords.slice(0, kAnchorWords).join(' '),
      anchorEnd: selectedWords.slice(-kAnchorWords).join(' '),
    }
  }

  // Very short selection — pad with surrounding context to reach kAnchorWords
  const padNeeded = kAnchorWords - selectedWords.length
  const startPad = beforeWords.slice(-padNeeded)
  const endPad = afterWords.slice(0, padNeeded)

  let anchorStartWords = [...startPad, ...selectedWords]
  let anchorEndWords = [...selectedWords, ...endPad]

  // Edge case: not enough context on one side — fall back to the other
  if (anchorStartWords.length === 0) {
    anchorStartWords = afterWords.slice(0, kAnchorWords)
  }
  if (anchorEndWords.length === 0) {
    anchorEndWords = beforeWords.slice(-kAnchorWords)
  }

  if (anchorStartWords.length === 0 && anchorEndWords.length === 0) return null

  return {
    anchorStart: anchorStartWords.join(' '),
    anchorEnd: anchorEndWords.join(' '),
  }
}
